import { useState } from "react";
import { cn } from "@/lib/utils";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

/** Niva's likeness, served from /public rather than the CDN it was authored on:
 *  the source URLs live under a per-user path in a generation tool's bucket, so
 *  a rotated account there would 404 her face in production. Both files are
 *  pre-cropped to a square and re-encoded small (193KB / 21KB) precisely so the
 *  avatar can be a video without costing a photo's worth of bytes per turn. */
const NIVA_AVATAR_VIDEO = "/niva/niva-avatar.mp4";
export const NIVA_AVATAR_POSTER = "/niva/niva-avatar.jpg";

/**
 * Niva — the face of the BL AI Deal Assistant chatbot.
 *
 * Real footage of her, not the illustrated SVG this used to draw. The clip is
 * a ping-pong loop (forward then reversed) so an idle avatar never jump-cuts
 * back to frame one.
 *
 * `animated` is opt-in, and deliberately not the default: the transcript puts
 * one avatar on every assistant turn, and a dozen decoding <video> elements
 * costs far more than a dozen <img> tags pointing at the same cached poster.
 * Pass it only where she is large and few — the header, the greeting portrait,
 * the launcher — and let the still frame carry the rest.
 */
export function NivaAvatar({
  className,
  animated = false,
}: {
  className?: string;
  animated?: boolean;
}) {
  const reducedMotion = usePrefersReducedMotion();
  // A codec or network failure should degrade to her poster, not to an empty
  // ring — <video> renders nothing at all when its source won't load.
  const [videoFailed, setVideoFailed] = useState(false);
  const showVideo = animated && !reducedMotion && !videoFailed;

  return (
    <span
      className={cn(
        "relative block shrink-0 overflow-hidden rounded-full bg-card ring-2 ring-primary/50",
        className,
      )}
    >
      {showVideo ? (
        <video
          src={NIVA_AVATAR_VIDEO}
          poster={NIVA_AVATAR_POSTER}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onError={() => setVideoFailed(true)}
          aria-label="Niva"
          role="img"
          className="h-full w-full object-cover"
        />
      ) : (
        <img
          src={NIVA_AVATAR_POSTER}
          alt="Niva"
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      )}
    </span>
  );
}

/**
 * Avatar + "online" dot, for the header and the launcher — the dot is a
 * sibling rather than part of the artwork so it keeps a crisp 2px ring against
 * whatever surface it sits on instead of scaling with her.
 */
export function NivaAvatarBadge({
  className,
  dotClassName,
  animated = false,
}: {
  className?: string;
  dotClassName?: string;
  animated?: boolean;
}) {
  return (
    <span className="relative inline-flex shrink-0">
      <NivaAvatar className={className} animated={animated} />
      <span
        className={cn(
          "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background bg-success",
          dotClassName,
        )}
        aria-hidden
      />
    </span>
  );
}

export default NivaAvatar;
